import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Users,
  Download,
  Ticket,
  DollarSign,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { toast } from "react-toastify";
import eventService from "../../../../api/EventService";
import Loader from "../../../../components/misc/Loader";
import {
  AttendeesTable,
  AttendeesFilterSection,
  CancelTicketModal,
  ViewAttendeeModal,
} from "./components";


/**
 * Event Attendees Management Page
 */
const EventAttendees = () => {
  const navigate = useNavigate();
  const { event_slug } = useParams();
  const location = useLocation();
  const event = location?.state?.event || null;

  // State management
  const [attendees, setAttendees] = useState([]);
  const [eventData, ] = useState(event);
  const [searchTerm, setSearchTerm] = useState("");
  const [ticketTypeFilter, setTicketTypeFilter] = useState("all");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedAttendee, setSelectedAttendee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);

  // Fetch event and attendees data
  useEffect(() => {
    if (event_slug) {
      fetchAttendees();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event_slug]);


  const fetchAttendees = async () => {
    setLoading(true);
    try {
      const response = await eventService.getEventAttendees(event_slug);
      if (response?.status) {
        let attendeesData = [];
        if (response.data.status_code === 200) {
          attendeesData = Array.isArray(response.data.data)
            ? response.data.data
            : [response.data.data];
        } else if (Array.isArray(response.data)) {
          attendeesData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          attendeesData = response.data.data;
        }
        setAttendees(attendeesData);
      } else {
        toast.error(response?.message || "Failed to fetch attendees");
        setAttendees([]);
      }
    } catch (error) {
      console.error("ERROR: ", error);
      toast.error(
        error.response?.data?.reason ||
          "An error occurred while fetching attendees"
      );
      setAttendees([]);
    } finally {
      setLoading(false);
    }
  };

  const ticketNameOf = (attendee) =>
    attendee?.ticket_type && typeof attendee.ticket_type === "object"
      ? attendee.ticket_type.name || ""
      : attendee?.ticket_type || "";

  const registrationCharge = (attendee) => {
    const ticket =
      attendee?.ticket_type && typeof attendee.ticket_type === "object"
        ? attendee.ticket_type
        : null;
    const currency = String(ticket?.currency || attendee?.currency || "").trim().toUpperCase();
    const hasTotal =
      attendee?.total_amount !== null &&
      attendee?.total_amount !== undefined &&
      attendee?.total_amount !== "";

    if (hasTotal) {
      const total = parseFloat(attendee.total_amount);
      return { amount: Number.isNaN(total) ? 0 : total, currency };
    }

    const unit = parseFloat(ticket?.price ?? attendee?.amount_paid ?? attendee?.price);
    const unitAmount = Number.isNaN(unit) ? 0 : unit;
    const isGroup =
      attendee?.registration_type === "group" ||
      (Array.isArray(attendee?.attendees) && attendee.attendees.length > 0);
    const quantity = isGroup
      ? (attendee.attendees?.length || Number(attendee.group_info?.number_of_attendees) || 1)
      : 1;

    return { amount: unitAmount * quantity, currency };
  };

  const registrationAmount = (attendee) => registrationCharge(attendee).amount;

  const registrationDisplayName = (attendee) => {
    if (attendee?.registration_type === "group" || attendee?.group_info) {
      return (
        attendee.group_info?.organization_name ||
        attendee.group_info?.primary_contact_name ||
        ""
      );
    }
    return [attendee?.first_name, attendee?.last_name].filter(Boolean).join(" ") || attendee?.name || "";
  };

  // Calculate statistics
  const stats = useMemo(() => {
    const total = attendees.reduce((sum, attendee) => {
      if (attendee.registration_type === "group") {
        return sum + (attendee.attendees?.length || attendee.group_info?.number_of_attendees || 1);
      }
      return sum + 1;
    }, 0);
    const revenueByCurrencyMap = new Map();
    attendees.forEach((attendee) => {
      const { amount, currency } = registrationCharge(attendee);
      const key = currency || "Unspecified";
      revenueByCurrencyMap.set(key, (revenueByCurrencyMap.get(key) || 0) + amount);
    });
    const revenueByCurrency = Array.from(revenueByCurrencyMap.entries())
      .map(([currency, amount]) => ({ currency, amount }))
      .sort((a, b) => a.currency.localeCompare(b.currency));
    const paid = attendees.filter(
      (a) => a.payment_status === "paid" || a.payment_status === "completed"
    ).length;
    const pending = attendees.filter(
      (a) => a.payment_status === "pending"
    ).length;

    return {
      total,
      revenueByCurrency,
      paid,
      pending,
    };
  }, [attendees]);

  // Get unique ticket types for filter
  const availableTicketTypes = useMemo(() => {
    const types = new Set();
    attendees.forEach((attendee) => {
      const name = ticketNameOf(attendee);
      if (name) types.add(name);
    });
    return Array.from(types).sort();
  }, [attendees]);

  // Filter and search logic
  const filteredAttendees = useMemo(() => {
    return attendees
      .filter((attendee) => {
        const query = searchTerm.toLowerCase();
        const memberText = (attendee.attendees || [])
          .map((person) =>
            [person.first_name, person.last_name, person.email, person.contact_number, person.profession]
              .filter(Boolean)
              .join(" ")
          )
          .join(" ");
        const searchable = [
          attendee.slug,
          attendee.attendee_id,
          registrationDisplayName(attendee),
          attendee.email,
          attendee.contact_number,
          attendee.phone,
          attendee.agent_code,
          attendee.group_info?.organization_name,
          attendee.group_info?.primary_contact_name,
          attendee.group_info?.primary_contact_email,
          attendee.group_info?.primary_contact_phone,
          attendee.user?.email,
          attendee.user?.first_name,
          attendee.user?.last_name,
          memberText,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        const matchesSearch = searchable.includes(query);

        const matchesTicketType =
          ticketTypeFilter === "all" ||
          ticketNameOf(attendee) === ticketTypeFilter;

        const matchesPaymentStatus =
          paymentStatusFilter === "all" ||
          attendee.payment_status === paymentStatusFilter;

        return matchesSearch && matchesTicketType && matchesPaymentStatus;
      })
      .sort((a, b) => {
        let aValue, bValue;

        switch (sortBy) {
          case "name":
            aValue = registrationDisplayName(a).toLowerCase();
            bValue = registrationDisplayName(b).toLowerCase();
            break;
          case "ticket_type":
            aValue = ticketNameOf(a).toLowerCase();
            bValue = ticketNameOf(b).toLowerCase();
            break;
          case "amount":
            aValue = registrationAmount(a);
            bValue = registrationAmount(b);
            break;
          case "date":
            aValue = new Date(a.created_at || a.purchased_at || 0);
            bValue = new Date(b.created_at || b.purchased_at || 0);
            break;
          default:
            aValue = a[sortBy] || "";
            bValue = b[sortBy] || "";
        }

        if (sortOrder === "asc") {
          return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
        } else {
          return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
        }
      });
  }, [
    attendees,
    searchTerm,
    ticketTypeFilter,
    paymentStatusFilter,
    sortBy,
    sortOrder,
  ]);

  const displayRows = useMemo(() => {
    const usesRegistrationType = filteredAttendees.some(
      (attendee) => attendee.registration_type || attendee.group_info || Array.isArray(attendee.attendees)
    );

    if (usesRegistrationType) {
      return filteredAttendees.map((attendee) => {
        const isGroup =
          attendee.registration_type === "group" ||
          (Array.isArray(attendee.attendees) && attendee.attendees.length > 0);
        if (isGroup) {
          return {
            type: "group",
            groupKey: attendee.slug || attendee.user_slug,
            registration: attendee,
            attendees: attendee.attendees || [],
          };
        }
        return { type: "single", attendee };
      });
    }

    const byKey = new Map();
    filteredAttendees.forEach((attendee) => {
      const key =
        attendee.booking_id ??
        attendee.group_id ??
        attendee.order_id ??
        `single-${attendee.attendee_id ?? attendee.id ?? attendee.ticket_id ?? attendee.email ?? ""}`;
      if (!byKey.has(key)) byKey.set(key, []);
      byKey.get(key).push(attendee);
    });
    const rows = [];
    byKey.forEach((groupAttendees, key) => {
      if (groupAttendees.length > 1) {
        rows.push({ type: "group", groupKey: key, attendees: groupAttendees });
      } else {
        rows.push({ type: "single", attendee: groupAttendees[0] });
      }
    });
    return rows;
  }, [filteredAttendees]);

  // Pagination over display rows (one row = one registration, single or group)
  const paginatedDisplayRows = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return displayRows.slice(startIndex, startIndex + itemsPerPage);
  }, [displayRows, currentPage, itemsPerPage]);

  // Handlers
  const handleViewAttendee = (attendee) => {
    setSelectedAttendee(attendee);
    setShowViewModal(true);
  };

  const handleCancelTicket = (attendee) => {
    setSelectedAttendee(attendee);
    setShowCancelModal(true);
  };

  const handleCancelConfirm = async (data) => {
    if (!selectedAttendee || !event_slug) return;

    try {
      const response = await eventService.cancelAttendeeTicket(
        event_slug,
        selectedAttendee.attendee_id || selectedAttendee.id,
        { reason: data.reason }
      );
      if (response.status) {
        toast.success(response.message || "Ticket cancelled successfully");
        setShowCancelModal(false);
        setSelectedAttendee(null);
        fetchAttendees();
      } else {
        toast.error(response.message || "Failed to cancel ticket");
      }
    } catch (error) {
      toast.error(error.message || "An error occurred");
    }
  };

  const handleExport = () => {
    // TODO: Implement CSV/Excel export
    toast.info("Export functionality coming soon");
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader />
        <p className="text-gray-600">Loading attendees...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-3 py-4 sm:px-4 sm:py-6 lg:px-6 lg:py-8 space-y-4 sm:space-y-6 lg:space-y-8">
        {/* Header */}
        <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
          <div className="flex-shrink-0">
            <h1 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-gray-900 flex items-center">
              <Users className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 mr-2 sm:mr-3 text-purple-600 flex-shrink-0" />
              <span className="truncate">Event Attendees</span>
            </h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">
              {eventData?.event_title || eventData?.venue_name || "Event"} - Manage attendees and tickets
            </p>
          </div>

          <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-3">
            <button
              onClick={fetchAttendees}
              className="bg-gray-100 text-gray-700 px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg hover:bg-gray-200 active:bg-gray-300 transition-colors flex items-center justify-center min-h-[44px] w-full sm:w-auto touch-manipulation"
            >
              <RefreshCw className="w-4 h-4 mr-2 flex-shrink-0" />
              <span>Refresh</span>
            </button>
            <button
              onClick={() => navigate(`/admin/media/events/view/${event_slug}`)}
              className="bg-white text-gray-700 px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg border border-gray-300 hover:bg-gray-50 active:bg-gray-100 transition-colors flex items-center justify-center min-h-[44px] w-full sm:w-auto touch-manipulation"
            >
              <ArrowLeft className="w-4 h-4 mr-2 flex-shrink-0" />
              <span>Back</span>
            </button>
            {/* <button
              onClick={handleExport}
              className="flex items-center justify-center px-3 py-2.5 sm:px-4 sm:py-3 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm transition-colors hover:bg-gray-50 min-h-[44px] w-full sm:w-auto touch-manipulation"
            >
              <Download className="mr-2 w-4 h-4 flex-shrink-0" />
              <span>Export</span>
            </button> */}
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm sm:p-5 lg:p-6">
          <div className="flex justify-between items-center">
            <div className="flex-1 min-w-0">
              <p className="mb-1 text-xs font-medium text-gray-600 sm:text-sm">
                Total Attendees
              </p>
              <p className="text-2xl font-bold text-purple-600 sm:text-3xl lg:text-4xl">
                {stats.total}
              </p>
            </div>
            <div className="flex-shrink-0 p-3 ml-3 bg-purple-50 rounded-xl">
              <Users className="w-5 h-5 text-purple-600 sm:w-6 sm:h-6" />
            </div>
          </div>
        </div>

        <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm sm:p-5 lg:p-6">
          <div className="flex justify-between items-center">
            <div className="flex-1 min-w-0">
              <p className="mb-1 text-xs font-medium text-gray-600 sm:text-sm">
                Total Revenue
              </p>
              {stats.revenueByCurrency.length === 0 ? (
                <p className="text-2xl font-bold text-green-600 sm:text-3xl">0.00</p>
              ) : (
                <div className="space-y-1">
                  {stats.revenueByCurrency.map(({ currency, amount }) => (
                    <p
                      key={currency}
                      className={`font-bold text-green-600 leading-tight ${
                        stats.revenueByCurrency.length > 1
                          ? "text-lg sm:text-xl"
                          : "text-2xl sm:text-3xl"
                      }`}
                    >
                      {currency === "Unspecified" ? "" : `${currency} `}
                      {amount.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </p>
                  ))}
                </div>
              )}
            </div>
            <div className="flex-shrink-0 p-3 ml-3 bg-green-50 rounded-xl">
              <DollarSign className="w-5 h-5 text-green-600 sm:w-6 sm:h-6" />
            </div>
          </div>
        </div>

        <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm sm:p-5 lg:p-6">
          <div className="flex justify-between items-center">
            <div className="flex-1 min-w-0">
              <p className="mb-1 text-xs font-medium text-gray-600 sm:text-sm">
                Paid Tickets
              </p>
              <p className="text-2xl font-bold text-blue-600 sm:text-3xl lg:text-4xl">
                {stats.paid}
              </p>
            </div>
            <div className="flex-shrink-0 p-3 ml-3 bg-blue-50 rounded-xl">
              <Ticket className="w-5 h-5 text-blue-600 sm:w-6 sm:h-6" />
            </div>
          </div>
        </div>

        <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm sm:p-5 lg:p-6">
          <div className="flex justify-between items-center">
            <div className="flex-1 min-w-0">
              <p className="mb-1 text-xs font-medium text-gray-600 sm:text-sm">
                Pending Payments
              </p>
              <p className="text-2xl font-bold text-orange-600 sm:text-3xl lg:text-4xl">
                {stats.pending}
              </p>
            </div>
            <div className="flex-shrink-0 p-3 ml-3 bg-orange-50 rounded-xl">
              <AlertCircle className="w-5 h-5 text-orange-600 sm:w-6 sm:h-6" />
            </div>
          </div>
        </div>
      </div>

        {/* Filters and Search */}
        <AttendeesFilterSection
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        ticketTypeFilter={ticketTypeFilter}
        onTicketTypeFilterChange={(value) => {
          setTicketTypeFilter(value);
          setCurrentPage(1);
        }}
        paymentStatusFilter={paymentStatusFilter}
        onPaymentStatusFilterChange={(value) => {
          setPaymentStatusFilter(value);
          setCurrentPage(1);
        }}
        sortBy={sortBy}
        onSortByChange={(value) => {
          setSortBy(value);
          setCurrentPage(1);
        }}
        sortOrder={sortOrder}
        onSortOrderChange={setSortOrder}
        onRefresh={fetchAttendees}
        ticketTypes={availableTicketTypes}
      />

        {/* Table Section */}
        <AttendeesTable
        displayRows={paginatedDisplayRows}
        onView={handleViewAttendee}
        onCancel={handleCancelTicket}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        totalCount={displayRows.length}
        onPageChange={setCurrentPage}
        onItemsPerPageChange={(value) => {
          setItemsPerPage(value);
          setCurrentPage(1);
        }}
        copiedId={copiedId}
        onCopy={setCopiedId}
      />

        {/* View attendee drawer */}
        <ViewAttendeeModal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedAttendee(null);
        }}
        attendee={selectedAttendee}
      />

        {/* Cancel Ticket Modal */}
        <CancelTicketModal
        isOpen={showCancelModal}
        onClose={() => {
          setShowCancelModal(false);
          setSelectedAttendee(null);
        }}
        onConfirm={handleCancelConfirm}
        attendee={selectedAttendee}
      />
      </div>
    </div>
  );
};

export default EventAttendees;

